import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

const TOKEN_KEY = "rmt_token";
const REFRESH_KEY = "rmt_refresh";
const PROFILE_KEY = "rmt_profile";

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveAuth = useCallback((idToken, refresh, prof) => {
    setProfile(prof);
    if (idToken) {
      localStorage.setItem(TOKEN_KEY, idToken);
      localStorage.setItem(REFRESH_KEY, refresh);
      localStorage.setItem(PROFILE_KEY, JSON.stringify(prof));
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      localStorage.removeItem(PROFILE_KEY);
    }
  }, []);

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use(async (config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) {
        config.headers.Authorization = `Bearer ${t}`;
      }
      return config;
    });
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          const refresh = localStorage.getItem(REFRESH_KEY);
          if (refresh) {
            try {
              const resp = await axios.post(`${API}/auth/refresh-token?refresh_token=${encodeURIComponent(refresh)}`);
              const newToken = resp.data.idToken;
              localStorage.setItem(TOKEN_KEY, newToken);
              localStorage.setItem(REFRESH_KEY, resp.data.refreshToken);
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return instance.request(error.config);
            } catch {
              localStorage.removeItem(TOKEN_KEY);
              localStorage.removeItem(REFRESH_KEY);
              localStorage.removeItem(PROFILE_KEY);
              setProfile(null);
            }
          } else {
            localStorage.removeItem(TOKEN_KEY);
            localStorage.removeItem(REFRESH_KEY);
            localStorage.removeItem(PROFILE_KEY);
            setProfile(null);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    const storedRefresh = localStorage.getItem(REFRESH_KEY);
    const storedProfile = localStorage.getItem(PROFILE_KEY);
    if (stored && storedProfile) {
      setProfile(JSON.parse(storedProfile));
      // Verify token is still valid
      axios.post(`${API}/auth/me`, {}, {
        headers: { Authorization: `Bearer ${stored}` },
      }).then(res => {
        setProfile(res.data);
        localStorage.setItem(PROFILE_KEY, JSON.stringify(res.data));
      }).catch(() => {
        if (storedRefresh) {
          axios.post(`${API}/auth/refresh-token?refresh_token=${encodeURIComponent(storedRefresh)}`)
            .then(resp => {
              localStorage.setItem(TOKEN_KEY, resp.data.idToken);
              localStorage.setItem(REFRESH_KEY, resp.data.refreshToken);
              return axios.post(`${API}/auth/me`, {}, {
                headers: { Authorization: `Bearer ${resp.data.idToken}` },
              });
            })
            .then(res => {
              setProfile(res.data);
              localStorage.setItem(PROFILE_KEY, JSON.stringify(res.data));
            })
            .catch(() => saveAuth(null, null, null));
        } else {
          saveAuth(null, null, null);
        }
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [saveAuth]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { idToken, refreshToken, profile: prof } = res.data;
    saveAuth(idToken, refreshToken, prof);
    return prof;
  }, [saveAuth]);

  const logout = useCallback(() => {
    saveAuth(null, null, null);
  }, [saveAuth]);

  const value = useMemo(() => ({
    profile, loading, login, logout, api,
    isAdmin: profile?.role === "admin",
  }), [profile, loading, login, logout, api]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
